import { LocalDateTime } from "@js-joda/core";
import React from "react";
import { isReminderRelevantForVisit } from "../clockInOutUtils";
import useStableCallback from "../hooks/useStableCallback";
import { isLoading, Loading } from "../Loading";
import { VisitInstanceDetails } from "../messages";
import { AppScheduledVisitsState } from "../system/AppState/AppScheduledVisitsState";
import { flmap } from "../utils/fmap";
import { List } from "../utils/List";
import { VisitReminderBottomSheetProps } from "./VisitReminderBottomSheet/VisitReminderBottomSheet";

interface Props {
  scheduledVisitsState: AppScheduledVisitsState | Loading;
  onRequestOpen: () => void;
  onRequestClose: () => void;
  onClickStartNow: (visit: VisitInstanceDetails) => void;
  children: (
    reminderProps: Omit<VisitReminderBottomSheetProps, "isOpen" | "presentVisit">,
    presentVisits: List<VisitInstanceDetails>
  ) => React.ReactNode;
}

const WithVisitReminderBottomSheet = (props: Props) => {
  const onRequestOpenVisitReminderRef = useStableCallback(props.onRequestOpen);
  const hasRequestedReminder = React.useRef(false);
  const now = LocalDateTime.now();

  const presentVisits = flmap(props.scheduledVisitsState, (visit) =>
    visit.scheduledVisitDetails.filter((x: VisitInstanceDetails) =>
      isReminderRelevantForVisit(x, now)
    )
  );

  React.useEffect(() => {
    if (hasRequestedReminder.current) {
      return;
    }

    onRequestOpenVisitReminderRef();
    hasRequestedReminder.current = true;
  }, [onRequestOpenVisitReminderRef, presentVisits]);

  const onClickStartNow = (visit: VisitInstanceDetails) => {
    props.onRequestClose();
    props.onClickStartNow(visit);
  };

  if (isLoading(presentVisits) || presentVisits.length() === 0) {
    return <></>;
  }

  return (
    <>
      {props.children(
        {
          onClickStartNow: onClickStartNow,
          onRequestClose: props.onRequestClose,
        },
        presentVisits
      )}
    </>
  );
};

export default WithVisitReminderBottomSheet;
